/**
 * HTML实体转义处理工具函数
 * 用于修复后台返回的转义字符，特别是SVG标签的转义问题
 */

/**
 * 解码HTML实体
 * 将 &lt; &gt; &amp; &quot; &#39; 等转义字符转换回原始字符
 * @param {string} str - 包含HTML实体的字符串
 * @returns {string} - 解码后的字符串
 */
export function decodeHtmlEntities(str) {
  if (!str || typeof str !== 'string') {
    return str;
  }
  
  const htmlEntities = {
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
    '&#x27;': "'",
    '&#x2F;': '/',
    '&#x60;': '`',
    '&#x3D;': '='
  };
  
  return str.replace(/&[a-zA-Z0-9#]+;/g, (entity) => {
    return htmlEntities[entity] || entity;
  });
}

/**
 * 专门处理SVG内容的转义问题
 * 检测并修复SVG标签的转义字符
 * @param {string} str - 包含可能转义SVG的字符串
 * @returns {string} - 修复后的字符串
 */
export function fixSvgEscaping(str) {
  if (!str || typeof str !== 'string') {
    return str;
  }
  
  // 检测是否包含转义的SVG标签
  const hasEscapedSvg = /&lt;svg|&lt;\/svg|&lt;path|&lt;g|&lt;text|&lt;rect|&lt;circle|&lt;line|&lt;polygon/i.test(str);
  
  if (hasEscapedSvg) {
    // 修复SVG相关的转义字符
    return str
      .replace(/&lt;svg/gi, '<svg')
      .replace(/&lt;\/svg/gi, '</svg')
      .replace(/&lt;path/gi, '<path')
      .replace(/&lt;\/path/gi, '</path')
      .replace(/&lt;g/gi, '<g')
      .replace(/&lt;\/g/gi, '</g')
      .replace(/&lt;text/gi, '<text')
      .replace(/&lt;\/text/gi, '</text')
      .replace(/&lt;rect/gi, '<rect')
      .replace(/&lt;\/rect/gi, '</rect')
      .replace(/&lt;circle/gi, '<circle')
      .replace(/&lt;\/circle/gi, '</circle')
      .replace(/&lt;line/gi, '<line')
      .replace(/&lt;\/line/gi, '</line')
      .replace(/&lt;polygon/gi, '<polygon')
      .replace(/&lt;\/polygon/gi, '</polygon')
      .replace(/&lt;defs/gi, '<defs')
      .replace(/&lt;\/defs/gi, '</defs')
      .replace(/&lt;style/gi, '<style')
      .replace(/&lt;\/style/gi, '</style')
      .replace(/&lt;clipPath/gi, '<clipPath')
      .replace(/&lt;\/clipPath/gi, '</clipPath')
      .replace(/&lt;use/gi, '<use')
      .replace(/&lt;\/use/gi, '</use')
      .replace(/&lt;image/gi, '<image')
      .replace(/&lt;\/image/gi, '</image')
      .replace(/&lt;foreignObject/gi, '<foreignObject')
      .replace(/&lt;\/foreignObject/gi, '</foreignObject');
  }
  
  return str;
}

/**
 * 智能修复HTML内容
 * 自动检测并修复各种HTML实体的转义问题
 * @param {string} str - 需要修复的HTML字符串
 * @returns {string} - 修复后的字符串
 */
export function smartFixHtml(str) {
  if (!str || typeof str !== 'string') {
    return str;
  }
  
  // 首先修复SVG转义
  let fixedStr = fixSvgEscaping(str);
  
  // 然后修复其他HTML实体
  fixedStr = decodeHtmlEntities(fixedStr);
  
  return fixedStr;
}

/**
 * 批量处理对象中的HTML内容
 * 递归处理对象中所有字符串属性，自动修复转义问题
 * @param {Object|Array} obj - 需要处理的对象或数组
 * @returns {Object|Array} - 处理后的对象或数组
 */
export function fixHtmlInObject(obj) {
  if (!obj) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => fixHtmlInObject(item));
  }
  
  if (typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        // 只处理可能包含HTML内容的字段
        if (key.includes('Title') || key.includes('Content') || key.includes('Answer') || 
            key.includes('Analysis') || key.includes('Description') || key.includes('Text')) {
          result[key] = smartFixHtml(value);
        } else {
          result[key] = value;
        }
      } else if (typeof value === 'object') {
        result[key] = fixHtmlInObject(value);
      } else {
        result[key] = value;
      }
    }
    return result;
  }
  
  return obj;
}

/**
 * 验证修复后的HTML内容
 * 检查SVG标签是否正确显示
 * @param {string} str - 修复后的HTML字符串
 * @returns {boolean} - 是否修复成功
 */
export function validateHtmlFix(str) {
  if (!str || typeof str !== 'string') {
    return false;
  }
  
  // 检查是否包含正确的SVG标签
  const hasValidSvg = /<svg[^>]*>.*<\/svg>/i.test(str);
  
  // 检查是否还包含转义字符
  const hasEscapedChars = /&lt;|&gt;|&amp;/.test(str);
  
  return hasValidSvg && !hasEscapedChars;
} 